// Copyright 2025, University of Maryland, All Rights Reserved
import { useCallback, useEffect, useMemo, useState, type FC } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  FullscreenDialog,
  FullscreenDialogContent,
  FullscreenDialogHeader,
  FullscreenDialogTitle,
  FullscreenDialogDescription,
  FullscreenDialogBody,
  Button,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/ui'
import { toast } from 'sonner'
import { ConfirmDialog } from '@/components/forms'
import { Download, ShieldAlert, ChevronDown, FileText, FileSpreadsheet } from 'lucide-react'

import { useServices } from '@/app-shell/services'
import { emitWorkspaceChange } from '@/app-shell/events'
import { useAnalysisStatuses, useDatasetPayload } from '@/applications/shared'
import type { NodeUserViewProps } from '@/core/types'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  DocumentPreviewProvider,
  type DocumentPreviewPayload,
} from './preview/PreviewContext'
import { DocumentAnalysisPanel } from './preview/AnalysisPanel'
import { SidebarToggle } from '@/app-shell/components/SidebarContext'
import { DashboardStatus } from './DashboardStatus'
import { DashboardTable } from './DashboardTable'
import { DashboardPagination } from './DashboardPagination'
import { DocumentUploadZone, useDocumentUpload } from '@/subsystems/documents'
import { VersionPanel } from './VersionPanel'
import { DatasetDiffViewer } from './diff-viewer'
import { DocsDialog } from './DocsDialog'
import { useTrackedOperation } from '@/subsystems/jobs'
import { runPipelines, useDatasets, usePipelinesList } from '@/subsystems/pipelines'
import { useWorkspaceConfig } from '@/app-shell/hooks'
import { agentFromRendererRef, useWorkspaceRenderers } from '@/subsystems/agents'
import type { DocumentListItemWithPipelines } from '../models'
import { useDashboardColumns } from '../hooks/useDashboardColumns'
import { PAGE_SIZE, DEFAULT_EMPTY } from '../utils/constants'
import { transformPipelinesToOptions } from '../utils/pipeline-datasets'
import {
  sortDocumentsByGroupId,
  extractUniqueYears,
  filterByYear,
} from '../utils/document'
import { workspaceDocumentsQueryKey } from '../api/workspaceDocumentsQuery'
import {
  getRedlinePdfUrl,
  getRedlineDocxUrl,
  downloadFromUrl,
} from '../utils/pdf-actions'
import { useAgents } from '@/subsystems/agents'

// Allow injecting pipelines data for testing (vi.mock doesn't work in browser mode)
interface ContractReviewUserViewProps extends NodeUserViewProps {
  wsId?: string
}

export const ContractReviewUserView: FC<ContractReviewUserViewProps> = ({
  workspaceId,
  orgId,
  wsId: wsIdProp,
}) => {
  const services = useServices()
  const apiClient = services.getClient()
  const resolvedOrgId = orgId && orgId.trim().length > 0 ? orgId : 'umd-org'
  const wsId = wsIdProp ?? workspaceId ?? 'default-workspace'

  const [message, setMessage] = useState<string | null>(null)
  const [previewTarget, setPreviewTarget] = useState<DocumentPreviewPayload | null>(null)
  const [pendingDelete, setPendingDelete] = useState<{ id: string; name: string; versionCount: number; groupId?: string } | null>(null)
  const [deleteInFlight, setDeleteInFlight] = useState(false)
  const [searchParams, setSearchParams] = useSearchParams()
  const versionPanelDocId = searchParams.get('version')
  // Version diff viewer state
  const [diffViewerTarget, setDiffViewerTarget] = useState<{ documentId: string; oldVersion: number; newVersion: number; agentId: string; datasetKey?: string } | null>(null)

  // Get workspace config (application settings) and pipelines separately
  const { applicationSettings, error: configError } = useWorkspaceConfig(resolvedOrgId, wsId)
  const { data: configPipelines, error: pipelinesQueryError } = usePipelinesList(resolvedOrgId, wsId)
  const { datasets: wsDatasets } = useDatasets(resolvedOrgId, wsId)
  const { data: agents = [] } = useAgents(apiClient, resolvedOrgId, wsId)
  const pipelinesData = configPipelines ?? null
  const pipelinesError = pipelinesQueryError ?? configError

  // Check for permission errors (403) - block access to entire view
  const is403Error = useMemo(() => {
    if (!pipelinesError) return false
    const err = pipelinesError as Error & { response?: { status?: number } }
    return err.response?.status === 403
  }, [pipelinesError])

  const renderers = useWorkspaceRenderers(apiClient, resolvedOrgId, wsId)

  // Application routes don't use instance_id (applications are singletons)
  const dashboardBasePath = useMemo(() => {
    if (!resolvedOrgId || !wsId) {
      return null
    }
    return `/org/${resolvedOrgId}/ws/${wsId}/contract-review`
  }, [resolvedOrgId, wsId])

  const documentsQueryKey = useMemo(
    () => workspaceDocumentsQueryKey(resolvedOrgId, wsId),
    [resolvedOrgId, wsId],
  )

  const queryClient = useQueryClient()
  const {
    data: documents = [],
    isPending: loading,
    error: queryError,
  } = useQuery<DocumentListItemWithPipelines[]>({
    queryKey: documentsQueryKey,
    enabled: Boolean(resolvedOrgId && wsId),
    queryFn: async () => {
      const params = new URLSearchParams({ include_pipeline_status: 'true' })
      const { data } = await apiClient.get<{ documents: DocumentListItemWithPipelines[] }>(
        `/org/${resolvedOrgId}/ws/${wsId}/documents?${params.toString()}`,
      )
      return data.documents ?? []
    },
  })

  const agentIds = useMemo(
    () => Array.from(new Set(Object.values(renderers).map(r => r.agentId))),
    [renderers],
  )

  const analysisStatuses = useAnalysisStatuses({
    apiClient,
    orgId: resolvedOrgId,
    workspaceId: wsId,
    agentIds,
  })

  const [error, setError] = useState<string | null>(null)
  useEffect(() => {
    if (queryError) setError(queryError instanceof Error ? queryError.message : String(queryError))
  }, [queryError])

  const refreshDocuments = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: documentsQueryKey })
    analysisStatuses.requestRefresh()
  }, [queryClient, documentsQueryKey, analysisStatuses])

  const totalCount = documents.length

  // Document upload via shared component
  const { uploadMultiple, uploading } = useDocumentUpload({
    apiClient,
    orgId: resolvedOrgId,
    wsId,
    onSuccess: refreshDocuments,
  })

  const handleUploadFiles = useCallback(async (files: File[]) => {
    await uploadMultiple(files)
  }, [uploadMultiple])

  const resolveRowKey = (doc: DocumentListItemWithPipelines) => doc.documentId

  const sortedDocuments = useMemo(
    () => sortDocumentsByGroupId(documents),
    [documents]
  )

  // Year filtering: show documents one year at a time, defaulting to most recent
  const [selectedYear, setSelectedYear] = useState<number | null>(null)

  // Pipeline selection state per document for batch actions
  const [pipelineSelections, setPipelineSelections] = useState<Map<string, Set<string>>>(new Map())

  // Version selection state per document/pipeline - lifted here to survive component remounts
  const [versionSelections, setVersionSelections] = useState<Map<string, Record<string, number>>>(new Map())

  const handleTogglePipelineSelection = useCallback((documentId: string, pipelineId: string) => {
    setPipelineSelections(prev => {
      const next = new Map(prev)
      const selected = new Set(prev.get(documentId) ?? [])
      if (selected.has(pipelineId)) {
        selected.delete(pipelineId)
      } else {
        selected.add(pipelineId)
      }
      next.set(documentId, selected)
      return next
    })
  }, [])

  const handleClearPipelineSelection = useCallback((documentId: string) => {
    setPipelineSelections(prev => {
      const next = new Map(prev)
      next.delete(documentId)
      return next
    })
  }, [])

  const handleVersionChange = useCallback((documentId: string, pipelineId: string, version: number) => {
    setVersionSelections(prev => {
      const next = new Map(prev)
      const docVersions = { ...(prev.get(documentId) ?? {}), [pipelineId]: version }
      next.set(documentId, docVersions)
      return next
    })
  }, [])

  const availableYears = useMemo(
    () => extractUniqueYears(sortedDocuments),
    [sortedDocuments]
  )

  // Auto-select most recent year when documents load
  useEffect(() => {
    if (availableYears.length > 0 && selectedYear === null) {
      setSelectedYear(availableYears[0])
    }
  }, [availableYears, selectedYear])

  const documentsToRender = useMemo(
    () => filterByYear(sortedDocuments, selectedYear),
    [sortedDocuments, selectedYear]
  )

  const availablePipelines = useMemo(
    () => transformPipelinesToOptions(pipelinesData ?? null),
    [pipelinesData]
  )

  useEffect(() => {
    if (!dashboardBasePath || !resolvedOrgId || !wsId) {
      return
    }
    setMessage(null)
    setPreviewTarget(null)
  }, [dashboardBasePath, resolvedOrgId, wsId])

  // Consolidated job tracking - automatically polls for completion
  const { runTracked, isRunning: running } = useTrackedOperation({
    apiClient,
    orgId: resolvedOrgId,
    workspaceId: wsId,
    onStatusChange: refreshDocuments,
    onAllComplete: () => {
      setMessage('All jobs completed')
    },
  })

  // Single function for all pipeline runs (main, version comparison, etc.)
  const runPipeline = useCallback(
    async (
      documentIds: string[],
      pipelineId?: string,
      targetVersion?: number,
      compareVersions?: { old: number; new: number }
    ) => {
      setMessage(null)
      setError(null)
      // Agent-owned pipelines resolve under their agent; find the owner by pipeline id.
      const agentId = pipelineId
        ? agents.find(a => a.pipelineId === pipelineId)?.agentId
        : undefined
      if (!agentId) throw new Error('Selected pipeline has no owning agent.')
      const runDocuments = documentIds.map((documentId) => {
        const document = documents.find(item => item.documentId === documentId)
        return {
          documentId,
          ...(document?.groupId ? { groupId: document.groupId } : {}),
        }
      })
      try {
        const result = await runTracked(
          () => runPipelines(apiClient, resolvedOrgId, wsId, 'contract-review', {
            documents: runDocuments,
            agentId,
            targetVersion,
            compareVersions,
          }),
          (response) => response.jobs.map(j => j.jobId)
        )
        const count = result.jobs.length
        if (count > 0) {
          setMessage(`Queued ${count} ${count === 1 ? 'document' : 'documents'}. Monitoring progress…`)
        } else {
          setMessage('Pipeline triggered.')
        }
        refreshDocuments()
        return result
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err)
        setError(msg)
        throw err
      }
    },
    [runTracked, apiClient, resolvedOrgId, wsId, agents, documents, refreshDocuments]
  )

  const findRowByDocumentId = useCallback(
    (documentId: string): DocumentListItemWithPipelines | undefined =>
      documents.find(doc => doc.documentId === documentId),
    [documents],
  )

  const handlePreviewRequest = useCallback(
    (payload: DocumentPreviewPayload) => {
      setPreviewTarget({
        ...payload,
        orgId: payload.orgId ?? resolvedOrgId,
        workspaceId: payload.workspaceId ?? wsId,
        nodeInstanceId: payload.nodeInstanceId ?? null,
        datasetKey: payload.datasetKey ?? null,
      })
    },
    [resolvedOrgId, wsId]
  )

  const handleClosePreview = useCallback((open: boolean) => {
    if (!open) setPreviewTarget(null)
  }, [])

  useEffect(() => {
    if (!previewTarget) {
      return
    }
    const stillExists = documentsToRender.some(doc => doc.documentId === previewTarget.documentId)
    if (!stillExists) {
      setPreviewTarget(null)
    }
  }, [previewTarget, documentsToRender])

  const handleRunDocument = useCallback((documentId: string, pipelineId?: string, targetVersion?: number) => {
    if (!documentId) {
      return
    }
    setMessage(null)
    void runPipeline([documentId], pipelineId, targetVersion)
  }, [runPipeline, setMessage])

  const handleOpenDocumentManager = useCallback((documentId: string) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      next.set('version', documentId)
      return next
    }, { replace: true })
  }, [setSearchParams])

  // Run version comparison - stores pending state, viewer opens when job completes
  const handleRunComparison = useCallback(async (documentId: string, pipelineId: string, oldVersion: number, newVersion: number) => {
    setMessage(null)
    setError(null)
    try {
      await runPipeline([documentId], pipelineId, undefined, { old: oldVersion, new: newVersion })
      // Don't open viewer yet - wait for job to complete via onAllComplete
    } catch {
      // Error already shown by runPipeline
    }
  }, [runPipeline])

  // View completed version comparison
  const handleViewComparison = useCallback((documentId: string, oldVersion: number, newVersion: number, agentId: string, datasetKey?: string) => {
    setDiffViewerTarget({ documentId, oldVersion, newVersion, agentId, datasetKey })
  }, [])

  // Delete document from Document Manager panel
  const handleDeleteFromPanel = useCallback((documentId: string) => {
    const doc = findRowByDocumentId(documentId)
    const filename = doc?.latestFilename ?? doc?.name ?? documentId
    const versionCount = doc?.latestVersion ?? 1
    const groupId = doc?.groupId ?? undefined
    // Close the panel first
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      next.delete('version')
      return next
    }, { replace: true })
    // Then trigger delete confirmation
    setPendingDelete({ id: documentId, name: filename, versionCount, groupId })
  }, [findRowByDocumentId, setSearchParams])

  const handleConfirmDelete = useCallback(async () => {
    if (!pendingDelete) return
    if (!dashboardBasePath) {
      setError('Workspace context unavailable')
      setPendingDelete(null)
      return
    }

    setDeleteInFlight(true)
    setError(null)
    setMessage(null)

    try {
      const groupParam = pendingDelete.groupId ? `?group_id=${encodeURIComponent(pendingDelete.groupId)}` : ''
      await apiClient.delete(`${dashboardBasePath}/documents/${encodeURIComponent(pendingDelete.id)}${groupParam}`)
      setMessage(`Deleted ${pendingDelete.name}`)
      refreshDocuments()
      emitWorkspaceChange({ orgId: resolvedOrgId, wsId, action: 'document-deleted' })
    } catch (err: any) {
      setError(err?.response?.data?.detail ?? err?.message ?? 'Failed to delete document')
    } finally {
      setDeleteInFlight(false)
      setPendingDelete(null)
    }
  }, [apiClient, dashboardBasePath, pendingDelete, refreshDocuments, resolvedOrgId, wsId])

  // Download annotated PDF via presigned URL
  const handleDownloadPdf = useCallback(async (
    documentId: string,
    agentId: string,
    datasetKey: string,
    filenamePrefix: string,
    version?: number,
    run?: number
  ) => {
    try {
      const url = await getRedlinePdfUrl(
        apiClient, resolvedOrgId, wsId, documentId, agentId, datasetKey,
        { download: true, version, run },
      )
      const doc = findRowByDocumentId(documentId)
      const name = doc?.latestFilename ?? doc?.name ?? documentId
      const baseName = name.replace(/\.pdf$/i, '')
      downloadFromUrl(url, `${filenamePrefix} - ${baseName}.pdf`)
    } catch (err) {
      console.error('[ContractReview] Failed to download annotated PDF:', err)
    }
  }, [apiClient, resolvedOrgId, wsId, findRowByDocumentId])

  // Download annotated DOCX with Word comments (only for DOCX uploads)
  const handleDownloadDocx = useCallback(async (documentId: string, agentId: string, datasetKey: string, filenamePrefix: string, version?: number, run?: number) => {
    try {
      const response = await getRedlineDocxUrl(
        apiClient, resolvedOrgId, wsId, documentId, agentId, datasetKey,
        { version, run },
      )

      const doc = findRowByDocumentId(documentId)
      const baseName = doc?.latestFilename ?? doc?.name?.replace(/\.(pdf|docx)$/i, '') ?? documentId

      const link = document.createElement('a')
      link.href = response.downloadUrl
      link.download = response.filename ?? `${filenamePrefix} - ${baseName}.docx`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } catch (err) {
      const status = (err as { response?: { status?: number } }).response?.status
      if (status === 400) {
        toast.warning('DOCX not available', {
          description: 'This document was not uploaded as a DOCX file. Use PDF download instead.',
        })
      } else if (status === 404) {
        toast.warning('DOCX not available', {
          description: 'Annotated DOCX not found. Re-run the analysis to generate it.',
        })
      } else {
        console.error('[ContractReview] Failed to download annotated DOCX:', err)
        toast.error('Download failed', { description: 'Could not download annotated DOCX' })
      }
    }
  }, [apiClient, resolvedOrgId, wsId, findRowByDocumentId, toast])

  // Pipeline id for the currently-mounted renderer comes from the agent named
  // in its ref. Renderer ids are agent-scoped, so searching agents for a bare
  // key would return whichever agent happened to be first. Renderer components
  // must read pipeline id from props (never hardcode it).
  const previewPipelineId = useMemo(() => {
    const ref = previewTarget?.pipelineId
    if (!ref) return undefined
    const agentId = agentFromRendererRef(ref)
    return agents.find(a => a.agentId === agentId)?.pipelineId ?? undefined
  }, [agents, previewTarget?.pipelineId])

  // Derive report info from renderer slot keys (not schemas).
  const reportInfo = useMemo(() => {
    if (!previewTarget?.pipelineId) return null
    const renderer = renderers[previewTarget.pipelineId]
    const datasetSlots = renderer?.slots.filter(s => s.type === 'dataset' && s.datasetKey) ?? []
    if (!datasetSlots.length || !renderer) return null

    const agentId = renderer.agentId
    const findingsSlot = datasetSlots.find(s => s.key === 'findings' || s.key === 'clauses')
    if (findingsSlot?.datasetKey) {
      return { type: 'findings' as const, agentId, datasetKey: findingsSlot.datasetKey, label: 'Download Redline', prefix: 'UMD Redlines' }
    }
    const changesSlot = datasetSlots.find(s => s.key === 'changes')
    if (changesSlot?.datasetKey) {
      return { type: 'findings' as const, agentId, datasetKey: changesSlot.datasetKey, label: 'Download Diff', prefix: 'UMD Version Analysis' }
    }
    const sectionsSlot = datasetSlots.find(s => s.key === 'sections')
    if (sectionsSlot?.datasetKey) {
      return { type: 'section' as const, agentId, datasetKey: sectionsSlot.datasetKey, label: 'Print Report', prefix: '' }
    }

    return null
  }, [previewTarget, renderers])

  // Download version diff annotated PDF for exactly the run on screen. Same
  // arguments as the open DatasetDiffViewer, so this shares its cache entry
  // rather than issuing a second request or re-resolving the run independently.
  const diffPayload = useDatasetPayload({
    apiClient,
    orgId: resolvedOrgId,
    wsId,
    documentId: diffViewerTarget?.documentId ?? '',
    agentId: diffViewerTarget?.agentId ?? '',
    datasetKey: diffViewerTarget?.datasetKey,
    version: diffViewerTarget?.newVersion ?? 0,
    comparedTo: diffViewerTarget?.oldVersion,
    enabled: diffViewerTarget !== null,
  })

  const handleDownloadVersionDiff = useCallback((documentId: string, agentId: string, datasetKey: string, newVersion: number, run?: number) => {
    void handleDownloadPdf(documentId, agentId, datasetKey, 'UMD Version Analysis', newVersion, run)
  }, [handleDownloadPdf])

  const columns = useDashboardColumns({
    availablePipelines,
    renderers,
    analysisStatuses,
    running,
    resolvedOrgId,
    wsId,
    basePath: dashboardBasePath,
    apiClient,
    onPreviewRequest: handlePreviewRequest,
    onRunDocument: handleRunDocument,
    onRunComparison: handleRunComparison,
    onViewComparison: handleViewComparison,
    onOpenDocumentManager: handleOpenDocumentManager,
    pipelineSelections,
    onTogglePipelineSelection: handleTogglePipelineSelection,
    onClearPipelineSelection: handleClearPipelineSelection,
    versionSelections,
    onVersionChange: handleVersionChange,
  })

  // Client-side pagination over the year-filtered list
  const [currentPageIndex, setCurrentPageIndex] = useState(0)
  useEffect(() => {
    setCurrentPageIndex(0)
  }, [selectedYear, documents.length])

  const pageCount = Math.max(1, Math.ceil(documentsToRender.length / PAGE_SIZE))
  const clampedPageIndex = Math.min(currentPageIndex, pageCount - 1)
  const pagedDocuments = useMemo(
    () => documentsToRender.slice(clampedPageIndex * PAGE_SIZE, (clampedPageIndex + 1) * PAGE_SIZE),
    [documentsToRender, clampedPageIndex],
  )

  const emptyLabel = (applicationSettings as any)?.emptyStateLabel ?? DEFAULT_EMPTY
  const title = (applicationSettings as any)?.title ?? 'Contract Review'
  const subtitle = `Workspace: ${wsId}`

  // Show access denied message when user lacks workspace permissions
  if (is403Error) {
    return (
      <div className="space-y-4">
        <Card className="border-border">
          <CardContent className="py-12">
            <div className="flex flex-col items-center justify-center text-center space-y-4">
              <div className="rounded-full bg-red-50 p-3">
                <ShieldAlert className="h-8 w-8 text-red-600" />
              </div>
              <div className="space-y-2">
                <h2 className="text-lg font-semibold text-slate-800">Access Denied</h2>
                <p className="text-sm text-slate-600 max-w-md">
                  You do not have permission to access this workspace.
                  Please contact your administrator if you believe this is an error.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <DocumentPreviewProvider onPreview={handlePreviewRequest}>
      <div className="space-y-4">
        <Card className="border-border">
          <CardHeader className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="space-y-2">
              <CardTitle className="text-xl font-semibold">{title}</CardTitle>
              <CardDescription className="text-sm text-slate-600">{subtitle}</CardDescription>
              <p className="text-sm text-slate-600">
                {selectedYear && documentsToRender.length !== totalCount
                  ? `${documentsToRender.length} documents in ${selectedYear} (${totalCount} total)`
                  : `Total documents: ${totalCount}`}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {availableYears.length > 1 && (
                <Select
                  value={selectedYear !== null ? String(selectedYear) : ''}
                  onValueChange={v => setSelectedYear(parseInt(v, 10))}
                >
                  <SelectTrigger className="w-[120px]">
                    <SelectValue placeholder="Year" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableYears.map(year => (
                      <SelectItem key={year} value={String(year)}>
                        {year}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <DocsDialog />
            </div>
          </CardHeader>
          <CardContent className="pb-6 space-y-4">
            <div data-testid="cr-upload-zone">
              <DocumentUploadZone
                onUpload={handleUploadFiles}
                uploading={uploading}
              />
            </div>

            <DashboardStatus message={message} error={error} />

            <DashboardTable
              rows={pagedDocuments}
              columns={columns}
              resolveRowId={resolveRowKey}
              selectionMode="none"
              emptyLabel={emptyLabel}
              loading={loading}
              renderMobileCard={(row) => {
                const col = (key: string) => columns.find(c => c.key === key)
                return (
                  <div className="rounded-lg border border-border bg-card p-4 space-y-3">
                    {/* Compact header: Doc ID + Uploaded By inline */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span className="font-mono">{row.groupId ?? '—'}</span>
                        {row.uploadedBy && (
                          <>
                            <span>·</span>
                            <span>{row.uploadedBy}</span>
                          </>
                        )}
                      </div>
                      {col('actions')?.render(row)}
                    </div>
                    {/* Document */}
                    <div>{col('document')?.render(row)}</div>
                    {/* Pipeline results */}
                    <div>{col('pipelines')?.render(row)}</div>
                  </div>
                )
              }}
            />

            <DashboardPagination
              currentPageIndex={clampedPageIndex}
              rowsOnPage={pagedDocuments.length}
              totalCount={documentsToRender.length}
              pageCountEstimate={pageCount}
              loading={loading}
              hasPreviousPage={clampedPageIndex > 0}
              canGoNext={clampedPageIndex < pageCount - 1}
              onPrevious={() => setCurrentPageIndex(i => Math.max(0, i - 1))}
              onNext={() => setCurrentPageIndex(i => Math.min(pageCount - 1, i + 1))}
            />
          </CardContent>
        </Card>

        <ConfirmDialog
          open={pendingDelete !== null}
          onOpenChange={(open) => { if (!open) setPendingDelete(null) }}
          title="Delete All Versions"
          description={
            pendingDelete
              ? `Are you sure you want to delete all ${pendingDelete.versionCount} version${pendingDelete.versionCount === 1 ? '' : 's'} of "${pendingDelete.name}"? This action cannot be undone.`
              : ''
          }
          confirmLabel="Delete All"
          cancelLabel="Cancel"
          variant="destructive"
          loading={deleteInFlight}
          onConfirm={handleConfirmDelete}
          testId="cr-delete-confirm"
        />

        <FullscreenDialog open={previewTarget !== null} onOpenChange={handleClosePreview}>
          <FullscreenDialogContent>
            <FullscreenDialogHeader
              leading={<SidebarToggle />}
              actions={
                <>
                  {/* Download dropdown - PDF or DOCX for findings/clause reports */}
                  {reportInfo?.type === 'findings' && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="sm" disabled={!wsDatasets || !reportInfo}>
                          <Download className="h-4 w-4 mr-2" />
                          {reportInfo?.label ?? 'Download'}
                          <ChevronDown className="h-4 w-4 ml-2" />
                        </Button>
                      </DropdownMenuTrigger>
                      {/* z-[10000] to render above FullscreenDialog's z-[9999] */}
                      <DropdownMenuContent align="end" className="z-[10000]">
                        <DropdownMenuItem
                          onClick={() => {
                            if (!previewTarget || !reportInfo) return
                            void handleDownloadPdf(previewTarget.documentId, reportInfo.agentId, reportInfo.datasetKey, reportInfo.prefix, previewTarget.version)
                          }}
                        >
                          <FileText className="h-4 w-4 mr-2" />
                          Download as PDF
                        </DropdownMenuItem>
                        {/* DOCX download only available for DOCX source files */}
                        {findRowByDocumentId(previewTarget?.documentId ?? '')?.latestFilename?.toLowerCase().endsWith('.docx') && (
                          <DropdownMenuItem
                            onClick={() => {
                              if (!previewTarget || !reportInfo) return
                              void handleDownloadDocx(previewTarget.documentId, reportInfo.agentId, reportInfo.datasetKey, reportInfo.prefix, previewTarget.version)
                            }}
                          >
                            <FileSpreadsheet className="h-4 w-4 mr-2" />
                            Download as DOCX
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </>
              }
            >
              <div className="flex flex-col gap-0.5">
                <FullscreenDialogTitle>{previewTarget?.title ?? 'Analysis Report'}</FullscreenDialogTitle>
                <FullscreenDialogDescription className="flex items-center gap-2">
                  {findRowByDocumentId(previewTarget?.documentId ?? '')?.latestFilename ?? findRowByDocumentId(previewTarget?.documentId ?? '')?.name ?? ''}
                  {previewTarget?.version && (
                    previewTarget.version === previewTarget.latestVersion ? (
                      <span className="text-xs font-medium text-slate-600 bg-slate-100 px-1.5 py-0.5 rounded">
                        v{previewTarget.version} (Latest)
                      </span>
                    ) : (
                      <span className="text-xs font-medium text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">
                        v{previewTarget.version}
                      </span>
                    )
                  )}
                </FullscreenDialogDescription>
              </div>
            </FullscreenDialogHeader>
            <FullscreenDialogBody>
              {previewTarget && previewTarget.pipelineId && renderers[previewTarget.pipelineId] && (
                <DocumentAnalysisPanel
                  renderer={renderers[previewTarget.pipelineId]}
                  documentId={previewTarget.documentId}
                  orgId={resolvedOrgId}
                  workspaceId={wsId}
                  pipelineId={previewPipelineId}
                  version={previewTarget.version}
                  documentName={findRowByDocumentId(previewTarget.documentId)?.latestFilename ?? findRowByDocumentId(previewTarget.documentId)?.name}
                />
              )}
            </FullscreenDialogBody>
          </FullscreenDialogContent>
        </FullscreenDialog>

        <VersionPanel
          documentId={versionPanelDocId}
          basePath={dashboardBasePath}
          apiClient={apiClient}
          onClose={() => setSearchParams(prev => {
            const next = new URLSearchParams(prev)
            next.delete('version')
            return next
          }, { replace: true })}
          onVersionUploaded={refreshDocuments}
          allowChanges
          onDeleteDocument={handleDeleteFromPanel}
        />

        {/* Version Diff Viewer Dialog */}
        <FullscreenDialog
          open={diffViewerTarget !== null}
          onOpenChange={(open) => { if (!open) setDiffViewerTarget(null) }}
        >
          <FullscreenDialogContent>
            <FullscreenDialogHeader
              leading={<SidebarToggle />}
              actions={
                <Button
                  variant="outline"
                  size="sm"
                  // Held until the payload resolves: the run it resolved to is
                  // what identifies the annotations, and without it the request
                  // would silently fall back to the newest run at this version.
                  disabled={!diffViewerTarget?.datasetKey || diffPayload.data?.run === undefined}
                  onClick={() => {
                    const run = diffPayload.data?.run
                    if (!diffViewerTarget?.datasetKey || run === undefined) return
                    handleDownloadVersionDiff(diffViewerTarget.documentId, diffViewerTarget.agentId, diffViewerTarget.datasetKey, diffViewerTarget.newVersion, run)
                  }}
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download AI Analysis
                </Button>
              }
            >
              <div className="flex flex-col gap-0.5">
                <FullscreenDialogTitle>Version Comparison</FullscreenDialogTitle>
                <FullscreenDialogDescription>
                  {diffViewerTarget && (
                    <>
                      {findRowByDocumentId(diffViewerTarget.documentId)?.latestFilename ?? findRowByDocumentId(diffViewerTarget.documentId)?.name ?? diffViewerTarget.documentId}
                      {' '}— v{diffViewerTarget.oldVersion} → v{diffViewerTarget.newVersion}
                    </>
                  )}
                </FullscreenDialogDescription>
              </div>
            </FullscreenDialogHeader>
            <FullscreenDialogBody>
              {diffViewerTarget && (
                <DatasetDiffViewer
                  documentId={diffViewerTarget.documentId}
                  oldVersion={diffViewerTarget.oldVersion}
                  newVersion={diffViewerTarget.newVersion}
                  orgId={resolvedOrgId}
                  workspaceId={wsId}
                  apiClient={apiClient}
                  agentId={diffViewerTarget.agentId}
                  datasetKey={diffViewerTarget.datasetKey}
                  className="h-full"
                />
              )}
            </FullscreenDialogBody>
          </FullscreenDialogContent>
        </FullscreenDialog>
      </div>
    </DocumentPreviewProvider>
  )
}
